// components/NotificationModal.js
import React from 'react';
import notificationService from '../services/notificationService';

/**
 * Компонент модального окна для отображения уведомлений
 * @param {Object} props - Свойства компонента
 * @param {Object} props.notification - Данные уведомления
 * @param {Function} props.onClose - Функция для закрытия модального окна
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const NotificationModal = ({ notification, onClose }) => {
  if (!notification) {
    return null;
  }

  // Определяем стили в зависимости от типа уведомления
  const getTypeStyles = () => {
    switch (notification.type) {
      case 'error':
        return {
          borderColor: 'border-red-500',
          bgColor: 'bg-red-50',
          textColor: 'text-red-800',
          iconColor: 'text-red-500',
          buttonColor: 'bg-red-600 hover:bg-red-700'
        };
      case 'warning':
        return {
          borderColor: 'border-yellow-500',
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-500',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        };
      case 'info':
      default:
        return {
          borderColor: 'border-blue-500',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-500',
          buttonColor: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };

  const styles = getTypeStyles();

  // Обработчик закрытия уведомления
  const handleClose = async () => {
    try {
      await notificationService.markAsRead(notification.id);
      onClose();
    } catch (error) {
      console.error('Ошибка при отметке уведомления как прочитанного:', error);
      // Закрываем модальное окно даже при ошибке
      onClose();
    }
  };

  // Иконка в зависимости от типа
  const getIcon = () => {
    switch (notification.type) {
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-lg shadow-xl max-w-md w-full border-2 ${styles.borderColor}`}>
        <div className={`p-6 ${styles.bgColor} rounded-t-lg`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 ${styles.iconColor} mr-3`}>
              {getIcon()}
            </div>
            <div className="flex-1">
              <h2 className={`text-xl font-bold ${styles.textColor} mb-2`}>
                {notification.title}
              </h2>
              <div className={`text-sm ${styles.textColor} whitespace-pre-wrap`}>
                {notification.message}
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white rounded-b-lg">
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className={`${styles.buttonColor} text-white px-6 py-2 rounded-md text-sm font-medium transition-colors`}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;

